import {
  Emphasis,
  MedicalHoService,
  MedicalScoreName,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import omit from 'lodash/fp/omit';
import { ScratchMhoSuggestionCategory } from '@/modules/scratchConsultation/scratchMhoSuggestion.service';
import { isNumeric } from '@ianesthesie-kit/core';
import { captureThrowOnDev, isEmpty } from '@/util';

export const QCODE_ALLERGY = 'Allergies';
export type ScratchMhoSuggestionId = string;

export type ScratchMhoSuggestionType =
  | 'basic'
  | 'bi-choice'
  | 'drawer'
  | 'clear';

export interface ScratchMhoSuggestionData {
  readonly code: string;
  readonly qcode?: string;
  readonly icd10Code?: string;
  readonly snomedCode?: string;
  readonly category?: ScratchMhoSuggestionCategory;
  notes?: string;
}

export type ScratchSuggestionPredicate = (se: SurgicalEvent) => boolean;

abstract class AbstractScratchMhoSuggestion<
  T extends ScratchMhoSuggestionType
> {
  protected constructor(
    readonly suggestionType: T,
    private readonly _translationOverride?: string,
    private readonly predicate?: ScratchSuggestionPredicate
  ) {}

  get translation(): string {
    return this._translationOverride ?? this.defaultTranslation;
  }

  abstract get id(): ScratchMhoSuggestionId;

  protected abstract get defaultTranslation(): string;

  applies(se?: SurgicalEvent): boolean {
    return !this.predicate || (!!se && this.predicate(se));
  }

  abstract toRequestBody(arg?: any): ScratchMhoSuggestionData[];
}

export class BasicScratchMhoSuggestion extends AbstractScratchMhoSuggestion<'basic'> {
  constructor(
    private _code: string,
    readonly qcode?: string,
    readonly snomedCode?: string,
    readonly icd10Code?: string,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate,
    readonly defaultEmphasis?: Emphasis
  ) {
    super('basic', translationOverride, predicate);
  }

  get id(): string {
    return `${this.qcode}-${this.code}`;
  }

  get code(): string {
    return this._code;
  }

  protected get defaultTranslation(): string {
    let c = this.code;
    if (c === 'YES') {
      c = 'Yes';
    } else if (c === 'NO') {
      c = 'No';
    } else if (c === 'NONE') {
      c = 'None';
    } else if (c.startsWith(`${this.qcode}.`)) {
      c = c.replace(`${this.qcode}.`, '');
    }
    return `medical_synthesis.TRIAGE_${this.qcode}.${c}`;
  }

  setCode(c: string) {
    if (!this.snomedCode) {
      captureThrowOnDev('Operation is only allowed for snomed suggestions');
      return;
    }
    this._code = c;
  }

  toRequestBody(): ScratchMhoSuggestionData[] {
    return [
      {
        code: this.code,
        qcode: this.qcode,
        icd10Code: this.icd10Code,
        snomedCode: this.snomedCode
      }
    ];
  }
}

export class NumericScratchMhoSuggestion extends BasicScratchMhoSuggestion {
  constructor(
    code: string,
    qcode: string,
    snomedCode?: string,
    icd10Code?: string,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate
  ) {
    super(code, qcode, snomedCode, icd10Code, translationOverride, predicate);
    if (!isNumeric(code)) {
      throw new Error(`Code is not numeric: ${code}`);
    }
  }

  protected get defaultTranslation(): string {
    return `medical_synthesis.TRIAGE_Q_${this.qcode}`;
  }
}

export class AllergyBasicScratchMhoSuggestion extends BasicScratchMhoSuggestion {
  constructor(
    code: string,
    snomedCode?: string,
    icd10Code?: string,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate,
    defaultEmphasis?: Emphasis
  ) {
    super(
      code,
      QCODE_ALLERGY,
      snomedCode,
      icd10Code,
      translationOverride,
      predicate,
      defaultEmphasis
    );
  }

  protected get defaultTranslation(): string {
    return `medical_synthesis.TRIAGE_${this.code}`;
  }
}

export class BiChoiceScratchMhoSuggestion extends AbstractScratchMhoSuggestion<'bi-choice'> {
  readonly leftChoice: Omit<ScratchMhoSuggestionData, 'qcode'>;
  readonly rightChoice: Omit<ScratchMhoSuggestionData, 'qcode'>;

  constructor(
    readonly qcode: string,
    leftChoice: string | Omit<ScratchMhoSuggestionData, 'qcode'>,
    rightChoice: string | Omit<ScratchMhoSuggestionData, 'qcode'>,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate
  ) {
    super('bi-choice', translationOverride, predicate);
    this.leftChoice =
      typeof leftChoice === 'string' ? { code: leftChoice } : leftChoice;
    this.rightChoice =
      typeof rightChoice === 'string' ? { code: rightChoice } : rightChoice;
  }

  get id(): string {
    return `${this.qcode}_${this.rightChoice.code}`;
  }
  protected get defaultTranslation(): string {
    return `medical_synthesis.TRIAGE_${this.rightChoice.code}`;
  }

  toRequestBody(choice?: 'left' | 'right') {
    if (choice === 'left') {
      return this.leftToRequestBody();
    }

    if (choice === 'right') {
      return this.rightToRequestBody();
    }

    throw new Error('Choice arg must be provided');
  }

  private leftToRequestBody(): ScratchMhoSuggestionData[] {
    return [
      {
        ...this.leftChoice,
        qcode: this.qcode
      }
    ];
  }

  private rightToRequestBody(): ScratchMhoSuggestionData[] {
    return [
      {
        ...this.rightChoice,
        qcode: this.qcode
      }
    ];
  }
}

export class YesNoChoiceScratchMhoSuggestion extends BiChoiceScratchMhoSuggestion {
  constructor(
    readonly qcode: string,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate
  ) {
    super(qcode, 'NO', 'YES', translationOverride, predicate);
  }

  protected get defaultTranslation(): string {
    return `medical_synthesis.TRIAGE_Q_${this.qcode}`;
  }
}

type YesNoDrawerSuggestionDataPayload = {
  type: 'yesNo';
  qcode: string;
  choices?: string[];
  multiple?: never;
  predicate?: (payload: Record<string, string | number | string[]>) => boolean;
};

type DateDrawerSuggestionDataPayload = {
  type: 'date';
  qcode: string;
  choices?: never;
  multiple?: never;
  predicate?: (payload: Record<string, string | number | string[]>) => boolean;
};

type RangeDrawerSuggestionDataPayload = {
  type: 'range';
  qcode: string;
  choices: number[];
  multiple?: never;
  predicate?: (payload: Record<string, string | number | string[]>) => boolean;
};

type EnumDrawerSuggestionDataPayload = {
  type: 'enum';
  qcode: string;
  choices: string[];
  multiple?: boolean;
  predicate?: (payload: Record<string, string | number | string[]>) => boolean;
};

export type DrawerSuggestionDataPayload =
  | YesNoDrawerSuggestionDataPayload
  | RangeDrawerSuggestionDataPayload
  | EnumDrawerSuggestionDataPayload
  | DateDrawerSuggestionDataPayload;

export type DrawerSuggestionData = {
  payload: ReadonlyArray<
    Omit<DrawerSuggestionDataPayload, 'choices'> & {
      choices?: (string | number)[];
    }
  >;
  notes?: string;
};

export class DrawerScratchMhoSuggestion extends AbstractScratchMhoSuggestion<'drawer'> {
  private static readonly YES_NO = ['YES', 'NO'];
  readonly _drawerData?: DrawerSuggestionData;

  constructor(
    readonly qcode: string,
    readonly code?: string,
    drawerDataPayload?: DrawerSuggestionDataPayload[],
    readonly allowNotes = true,
    translationOverride?: string,
    predicate?: ScratchSuggestionPredicate
  ) {
    super('drawer', translationOverride, predicate);
    if (drawerDataPayload) {
      this._drawerData = {
        payload: drawerDataPayload.map((p) => ({
          ...p,
          choices:
            p.type === 'yesNo' && !p.choices
              ? DrawerScratchMhoSuggestion.YES_NO
              : p.choices
        }))
      };
    }
  }

  get id(): string {
    return `${this.qcode}`;
  }

  protected get defaultTranslation(): string {
    return `doctor-dashboard.scratch.drawer.title.${this.qcode}`;
  }

  static uniquifyQcode(p: Partial<DrawerSuggestionDataPayload>) {
    return p.type === 'yesNo' || p.type === 'enum'
      ? `${p.qcode}___${p.choices?.[0]}`
      : p.qcode;
  }

  static qcodeFromUniquified(str: string) {
    return str.split('___')[0];
  }

  getDrawerData(
    payloadChoices: Record<string, string | number | string[]> = {}
  ) {
    return this._drawerData?.payload.filter(
      (p) => !p.predicate || p.predicate(payloadChoices)
    );
  }

  toRequestBody(arg?: {
    payloadChoices: Record<string, string | number | string[]>;
    notes?: string;
  }): ScratchMhoSuggestionData[] {
    if (!arg) {
      throw new Error('An argument is required');
    }
    const { payloadChoices, notes } = arg;
    const res: ScratchMhoSuggestionData[] = [];
    if (this.code) {
      res.push({ qcode: this.qcode, code: this.code });
    }
    this.choicesValidOrThrow(payloadChoices);

    Object.entries(payloadChoices!)
      .map(
        ([key, val]) =>
          [DrawerScratchMhoSuggestion.qcodeFromUniquified(key), val] as [
            string,
            string | number | string[]
          ]
      )
      .filter(
        ([qcode]) => !!this._drawerData!.payload.find((p) => p.qcode === qcode)
      )
      .forEach(([qcode, code]) => {
        if (Array.isArray(code)) {
          code.forEach((c) => res.push({ qcode, code: c as any }));
        } else {
          res.push({ qcode, code: code as any });
        }
      });

    if (notes && res.length) {
      res[0].notes = notes;
    }

    return res;
  }

  choicesValid(payloadChoices?: Record<string, string | number | string[]>) {
    if (!this._drawerData?.payload) {
      return true;
    }

    if (isEmpty(payloadChoices)) {
      console.log(1);
      throw false;
    }

    if (Object.keys(payloadChoices).length > this._drawerData.payload.length) {
      console.log(2);
      return false;
    }

    return true;
  }

  private choicesValidOrThrow(
    payloadChoices?: Record<string, string | number | string[]>
  ): void | never {
    if (!this.choicesValid(payloadChoices)) {
      throw new Error('Drawer payload is not valid');
    }
  }
}

export class ScoreScratchMhoSuggestion extends DrawerScratchMhoSuggestion {
  constructor(
    readonly score: MedicalScoreName,
    predicate?: ScratchSuggestionPredicate
  ) {
    super(
      MedicalHoService.getQcodeByScoreName(score) ?? score,
      undefined,
      undefined,
      true,
      undefined,
      predicate
    );
  }

  get id(): string {
    return `${this.qcode}`;
  }
  protected get defaultTranslation(): string {
    return `doctor-dashboard.medicalScore.${this.score}`;
  }

  toRequestBody(): ScratchMhoSuggestionData[] {
    throw new Error('Not supported for scores');
  }
}

export class ClearScratchMhoSuggestion extends AbstractScratchMhoSuggestion<'clear'> {
  private _id: string;

  constructor(
    id: string,
    private onClearAdd: (ScratchMhoSuggestionData & {
      predicate?: ScratchSuggestionPredicate;
      isScore?: boolean;
    })[],
    translationOverride = 'doctor-dashboard.scratch.clear',
    readonly sticky = true,
    predicate?: ScratchSuggestionPredicate
  ) {
    super('clear', translationOverride, predicate);
    this._id = id;
  }

  get id(): string {
    return this._id;
  }

  protected get defaultTranslation(): string {
    if (this.onClearAdd.length === 1) {
      let c = this.onClearAdd[0].code;
      if (c === 'YES') {
        c = 'Yes';
      } else if (c === 'NO') {
        c = 'No';
      } else if (c === 'NONE') {
        c = 'None';
      }
      return `medical_synthesis.TRIAGE_${this.onClearAdd[0].qcode}.${c}`;
    }
    throw new Error(
      'A translation override should be provided if onClearAdd.length > 1'
    );
  }

  toRequestBody(se?: SurgicalEvent): ScratchMhoSuggestionData[] {
    return this.onClearAdd
      .filter(({ predicate }) => !se || !predicate || predicate(se))
      .filter(({ isScore }) => !isScore)
      .map((data) => omit('predicate', data));
  }

  getScoresToClear(se?: SurgicalEvent): MedicalScoreName[] {
    return this.onClearAdd
      .filter(({ predicate }) => !se || !predicate || predicate(se))
      .filter(({ isScore }) => !!isScore)
      .map(({ qcode }) => MedicalHoService.getScoreNameByQcode(qcode)!)
      .filter(Boolean);
  }
}

export type ScratchSuggestion =
  | BasicScratchMhoSuggestion
  | NumericScratchMhoSuggestion
  | AllergyBasicScratchMhoSuggestion
  | BiChoiceScratchMhoSuggestion
  | YesNoChoiceScratchMhoSuggestion
  | DrawerScratchMhoSuggestion
  | ScoreScratchMhoSuggestion
  | ClearScratchMhoSuggestion;
